/**
 * `/lehrer/sets/neu?spiel=<gameId>` and `/lehrer/sets/:id`: the editor
 * shell around a module's set editor.
 *
 * The shell owns what every format shares: the title, saving (create, then
 * update), drafts, validation messages, JSON import and export, and a raw
 * JSON tab that always works, even for formats without a visual editor.
 * The module's `editor` entry point renders the visual editor and calls
 * `context.save(set)` after every change.
 *
 * Saving: incomplete sets are stored as drafts (ADR 0010). The status line
 * says whether the set is playable; "Was fehlt noch?" lists open points and
 * asks the editor to jump to each one.
 */
import type { GameInfo, MediaInfo } from '@schulspiele/protocol';

import { api, ApiError } from '@shared/api';
import { esc, qs } from '@shared/dom';
import type { EditorContext, EditorMounted, SaveOutcome, SetIssue } from '@shared/game-client';
import { t, type MessageKey } from '@shared/i18n/de';
import { createLifetime } from '@shared/lifetime';
import { mediaUrl } from '@shared/media';
import { openMediaPicker } from '@shared/ui/media-library';
import { BANK_TAKE_KEY, loadCatalog } from '@shared/bank';
import type { BankQuestion } from '@tasks/index';
import { toast, toastError } from '@shared/ui/toast';
import { loadGameClient } from '../../games';
import { workLayout } from '../layout';
import { currentTeacher, renderLogin } from './common';
import { downloadBlob } from '@shared/download';

interface SetRecord {
  id: string;
  gameId: string;
  title: string;
  set: unknown;
  complete: boolean;
  openIssues: number;
  issues?: SetIssue[];
}

const TITLE_SAVE_DELAY = 900;

export function renderEditorPage(
  mount: HTMLElement,
  setId: string | null,
  query: URLSearchParams,
): () => void {
  const life = createLifetime();
  let editor: EditorMounted | null = null;
  let titleTimer = 0;
  let leaving = false;
  let titleSave: (() => void) | null = null;

  void (async () => {
    const teacher = await currentTeacher();
    if (life.isDisposed()) return;
    if (teacher === null) {
      renderLogin(mount, () => {
        renderEditorPage(mount, setId, query);
      });
      return;
    }

    let record: SetRecord | null = null;
    let gameId = query.get('spiel') ?? '';
    try {
      if (setId !== null) {
        record = await api<SetRecord>(`/api/sets/${encodeURIComponent(setId)}`);
        gameId = record.gameId;
      }
    } catch {
      workLayout(mount, `<div class="muted-box">${t('editor.notFound')}</div>`, {
        teacherName: teacher.displayName,
      });
      return;
    }
    let game: GameInfo;
    try {
      game = (await api<{ game: GameInfo }>(`/api/games/${encodeURIComponent(gameId)}`)).game;
    } catch {
      workLayout(mount, `<div class="muted-box">${t('format.notFound')}</div>`, {
        teacherName: teacher.displayName,
      });
      return;
    }
    const client = await loadGameClient(gameId);
    if (life.isDisposed()) return;
    const hasVisualEditor = client?.editor !== undefined;

    let currentSet: unknown = record?.set ?? null;
    let currentId = record?.id ?? null;
    let saving: Promise<SaveOutcome> | null = null;
    let queued: unknown = undefined;
    let waiting: ((outcome: SaveOutcome) => void)[] = [];

    const main = workLayout(
      mount,
      `<div class="page-head">
         <h1>${esc(game.name)}: ${record === null ? t('editor.newTitle') : t('editor.editTitle')}</h1>
         <a class="btn btn--ghost btn--small" href="/lehrer" data-link>${t('session.back')}</a>
       </div>
       <div class="editor-head">
         <label class="field">${t('editor.title')}<input name="title" maxlength="120" value="${esc(record?.title ?? '')}" placeholder="${t('editor.titlePlaceholder')}" /></label>
         <div class="actions">
           <span class="save-state" data-save-state aria-live="polite"></span>
           <button class="btn btn--ghost" type="button" data-save>${t('editor.save')}</button>
         </div>
       </div>
       <ul class="issues" data-issues hidden></ul>
       <details class="open-issues" data-open hidden>
         <summary data-open-summary></summary>
         <ul data-open-list></ul>
       </details>
       <div class="tabs" role="tablist">
         <button type="button" role="tab" data-tab="visual" ${hasVisualEditor ? '' : 'hidden'}>${t('editor.tabVisual')}</button>
         <button type="button" role="tab" data-tab="json">${t('editor.tabJson')}</button>
       </div>
       <div data-panel="visual" role="tabpanel"></div>
       <div data-panel="json" role="tabpanel" hidden>
         <div class="stack--tight">
           <label class="field">${t('editor.jsonLabel')}<textarea spellcheck="false" data-json></textarea></label>
           <div class="actions">
             <button class="btn btn--small" type="button" data-apply>${t('editor.applyJson')}</button>
             <label class="btn btn--ghost btn--small">${t('editor.import')}<input type="file" accept="application/json,.json" data-import hidden /></label>
             <button class="btn btn--ghost btn--small" type="button" data-export>${t('editor.export')}</button>
           </div>
           ${hasVisualEditor ? '' : `<p class="muted">${t('editor.noVisual')}</p>`}
         </div>
       </div>`,
      { teacherName: teacher.displayName },
    );

    const titleInput = qs<HTMLInputElement>(main, 'input[name="title"]');
    const jsonArea = qs<HTMLTextAreaElement>(main, '[data-json]');
    const blockingEl = qs(main, '[data-issues]');
    const openEl = qs<HTMLDetailsElement>(main, '[data-open]');
    const saveState = qs(main, '[data-save-state]');
    jsonArea.value = JSON.stringify(currentSet ?? {}, null, 2);

    /* ---- Status and issues ---------------------------------------------- */

    const setState = (
      kind: 'saving' | 'saved' | 'draft' | 'failed' | 'unsaved',
      text: string,
    ): void => {
      saveState.dataset.state = kind;
      saveState.innerHTML = `<span class="save-state__icon" aria-hidden="true"></span>${esc(text)}`;
    };

    const showIssues = (issues: SetIssue[]): void => {
      const blocking = issues.filter((i) => i.severity !== 'incomplete');
      const open = issues.filter((i) => i.severity === 'incomplete');
      blockingEl.hidden = blocking.length === 0;
      // The message says where; the same one twice is one line.
      blockingEl.innerHTML = [...new Set(blocking.map((i) => i.message))]
        .map((m) => `<li>${esc(m)}</li>`)
        .join('');
      openEl.hidden = open.length === 0;
      qs(openEl, '[data-open-summary]').textContent = t('editor.openIssues', {
        count: open.length,
      });
      qs(openEl, '[data-open-list]').innerHTML = open
        .map(
          (i) =>
            `<li><button type="button" class="open-issues__item" data-path="${esc(i.path)}">${esc(i.message)}</button></li>`,
        )
        .join('');
    };
    openEl.addEventListener('click', (e) => {
      const b = (e.target as HTMLElement).closest<HTMLElement>('[data-path]');
      if (b === null) return;
      if (editor?.focus !== undefined && !qs(main, '[data-panel="visual"]').hidden) {
        editor.focus(b.dataset.path ?? '');
      } else {
        selectTab('json');
      }
    });

    if (record !== null) {
      setState(
        record.complete ? 'saved' : 'draft',
        record.complete ? t('editor.ready') : t('editor.draft', { count: record.openIssues }),
      );
    }

    /* ---- Saving ------------------------------------------------------------ */

    const saveNow = async (set: unknown): Promise<SaveOutcome> => {
      const title = titleInput.value.trim();
      setState('saving', t('editor.saving'));
      try {
        let saved: SetRecord;
        if (currentId === null) {
          saved = await api<SetRecord>('/api/sets', {
            method: 'POST',
            body: { gameId, title, set },
            keepalive: leaving,
          });
          currentId = saved.id;
          history.replaceState(null, '', `/lehrer/sets/${saved.id}`);
        } else {
          saved = await api<SetRecord>(`/api/sets/${encodeURIComponent(currentId)}`, {
            method: 'PUT',
            body: { title, set },
            keepalive: leaving,
          });
        }
        currentSet = set;
        const issues = saved.issues ?? [];
        showIssues(issues);
        if (saved.complete) setState('saved', t('editor.ready'));
        else setState('draft', t('editor.draft', { count: issues.length }));
        return { saved: true, complete: saved.complete, issues };
      } catch (err) {
        const issues: SetIssue[] =
          err instanceof ApiError && err.status === 422
            ? ((err.body as { issues?: SetIssue[] }).issues ?? [])
            : [{ path: '', message: t('error.generic') }];
        showIssues(issues);
        setState('failed', t('editor.notSaved'));
        return { saved: false, complete: false, issues };
      }
    };

    /**
     * Serialise saves: while one request is running, keep only the newest
     * set and send it afterwards. The editor may call this on every keystroke
     * (debounced); the server sees at most one request at a time.
     */
    const save = (set: unknown): Promise<SaveOutcome> => {
      // Leaving the page: a queued save would never be sent, so an existing
      // set goes out now as a keepalive request (it reaches the server after
      // the running one). A brand-new set with its first POST still running
      // has no id yet; that last change is lost rather than duplicating the set.
      if (leaving && saving !== null && currentId !== null) return saveNow(set);
      if (saving === null) {
        saving = saveNow(set).finally(() => {
          saving = null;
          flushQueued();
        });
        return saving;
      }
      // A save is running: remember only the newest set; everyone waiting
      // gets the outcome of that one.
      queued = set;
      return new Promise((resolve) => waiting.push(resolve));
    };
    const flushQueued = (): void => {
      if (queued === undefined) return;
      const next = queued;
      const resolvers = waiting;
      queued = undefined;
      waiting = [];
      void save(next).then((outcome) => {
        for (const r of resolvers) r(outcome);
      });
    };

    /* ---- Tabs ---------------------------------------------------------------- */

    const selectTab = (name: 'visual' | 'json'): void => {
      for (const b of main.querySelectorAll<HTMLElement>('[data-tab]')) {
        b.setAttribute('aria-selected', String(b.dataset.tab === name));
      }
      qs(main, '[data-panel="visual"]').hidden = name !== 'visual';
      qs(main, '[data-panel="json"]').hidden = name !== 'json';
      if (name === 'json')
        jsonArea.value = JSON.stringify(currentSet ?? editor?.current?.() ?? {}, null, 2);
    };
    for (const b of main.querySelectorAll<HTMLElement>('[data-tab]')) {
      b.addEventListener('click', () => {
        selectTab(b.dataset.tab === 'json' ? 'json' : 'visual');
      });
    }

    /* ---- The module's visual editor --------------------------------------- */

    if (client?.editor !== undefined) {
      const context: EditorContext = {
        role: 'teacher',
        actorId: teacher.id,
        clockOffset: 0,
        t: (key, params) => t(key as MessageKey, params),
        set: record?.set ?? null,
        save: (set) => {
          currentSet = set;
          return save(set);
        },
        pickMedia: (kind) => openMediaPicker(kind),
        mediaUrl: (id, variant) => mediaUrl(id, variant ?? 'original'),
        describeMedia: async (ids) => {
          const wanted = new Set(ids);
          const { media } = await api<{ media: MediaInfo[] }>('/api/media');
          return media.filter((m) => wanted.has(m.id));
        },
        pickFromBank: async (types, room, options) => {
          // The picker brings the task kit's previews: loaded when used.
          const { openBankPicker } = await import('@shared/bank-picker');
          const picked = await openBankPicker({
            types,
            ...(room === undefined ? {} : { room }),
            ...(options?.media === undefined ? {} : { media: options.media }),
            ...(options?.note === undefined ? {} : { note: options.note }),
            // Only what this game takes: counts and draws match what is taken (U19).
            game: gameId,
          });
          if (picked === null) return null;
          if (picked.skipped > 0) {
            toast(
              `${String(picked.skipped)} ${picked.skipped === 1 ? 'Frage ließ' : 'Fragen ließen'} sich nicht übernehmen.`,
              'info',
            );
          }
          return picked.questions;
        },
        saveToBank: async (input) => {
          try {
            const { openBankItemDialog } = await import('@shared/bank-item-dialog');
            const saved = await openBankItemDialog({
              catalog: await loadCatalog(),
              isAdmin: teacher.isAdmin,
              initial: input,
              title: 'In die Fragenbibliothek legen',
              sortingOnly: true,
            });
            if (saved === null) return false;
            toast(
              saved.complete
                ? 'Die Frage liegt jetzt in der Fragenbibliothek.'
                : 'In der Fragenbibliothek, als Entwurf: es fehlt noch etwas.',
              'success',
            );
            return true;
          } catch (err) {
            toastError(err);
            return false;
          }
        },
      };
      editor = client.editor(qs(main, '[data-panel="visual"]'), context);
      selectTab('visual');
      if (query.get('aus') === 'bibliothek') void takePending(editor);
    } else {
      selectTab('json');
    }

    /** Questions the Fragenbibliothek page sent along ("In Set übernehmen"). */
    async function takePending(target: EditorMounted): Promise<void> {
      const read = (): { gameId?: unknown; ids?: unknown } | null => {
        try {
          const raw = sessionStorage.getItem(BANK_TAKE_KEY);
          sessionStorage.removeItem(BANK_TAKE_KEY);
          return JSON.parse(raw ?? 'null') as { gameId?: unknown; ids?: unknown } | null;
        } catch {
          return null;
        }
      };
      const pending = read();
      const url = new URL(location.href);
      url.searchParams.delete('aus');
      history.replaceState(null, '', url.pathname + url.search);
      if (pending?.gameId !== game.id || !Array.isArray(pending.ids)) return;
      if (target.insertFromBank === undefined) return;
      try {
        const { questions, skipped } = await api<{ questions: BankQuestion[]; skipped: number }>(
          '/api/bank/take',
          { method: 'POST', body: { ids: pending.ids } },
        );
        const result = await target.insertFromBank(questions);
        const left = result.skipped + skipped;
        toast(
          `${String(result.added)} ${result.added === 1 ? 'Frage' : 'Fragen'} aus der Bibliothek übernommen${left > 0 ? `, ${String(left)} passten nicht` : ''}.`,
          result.added > 0 ? 'success' : 'info',
        );
      } catch (err) {
        toastError(err);
      }
    }

    /* ---- Title, save button, JSON ------------------------------------------- */

    titleSave = () => {
      const set = editor?.current?.() ?? currentSet ?? null;
      if (set !== null) void save(set);
    };
    titleInput.addEventListener('input', () => {
      setState('unsaved', t('editor.unsaved'));
      window.clearTimeout(titleTimer);
      // With a visual editor the title saves on its own like everything else.
      if (hasVisualEditor) {
        titleTimer = window.setTimeout(() => {
          titleTimer = 0;
          titleSave?.();
        }, TITLE_SAVE_DELAY);
      }
    });

    const parseJson = (): unknown => {
      try {
        return JSON.parse(jsonArea.value) as unknown;
      } catch {
        showIssues([{ path: '', message: t('editor.badJson') }]);
        return undefined;
      }
    };

    qs(main, '[data-save]').addEventListener('click', () => {
      const fromJson = !qs(main, '[data-panel="json"]').hidden;
      const set = fromJson ? parseJson() : (currentSet ?? editor?.current?.() ?? null);
      if (set === undefined) return;
      void save(set ?? {}).then((outcome) => {
        if (outcome.saved) {
          if (fromJson) editor?.update(set);
          toast(
            outcome.complete
              ? t('editor.ready')
              : t('editor.draft', { count: outcome.issues.length }),
            'success',
          );
        }
      });
    });

    // Apply the JSON to the visual editor (and save it).
    qs(main, '[data-apply]').addEventListener('click', () => {
      const set = parseJson();
      if (set === undefined) return;
      void save(set).then((outcome) => {
        if (!outcome.saved) return;
        editor?.update(set);
        if (hasVisualEditor) selectTab('visual');
        toast(t('editor.applied'), 'success');
      });
    });

    jsonArea.addEventListener('input', () => {
      setState('unsaved', t('editor.unsaved'));
    });

    qs<HTMLInputElement>(main, '[data-import]').addEventListener('change', (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file === undefined) return;
      void file
        .text()
        .then((text) => {
          const parsed = JSON.parse(text) as unknown;
          // Accept both a bare set and an export wrapper { title, gameId, set }.
          const wrapper = parsed as { title?: unknown; gameId?: unknown; set?: unknown } | null;
          let imported: unknown = parsed;
          if (typeof wrapper?.gameId === 'string' && wrapper.set !== undefined) {
            if (wrapper.gameId !== gameId) throw new Error(t('editor.wrongGame'));
            if (typeof wrapper.title === 'string' && titleInput.value.trim() === '')
              titleInput.value = wrapper.title;
            imported = wrapper.set;
          }
          selectTab('json');
          // selectTab shows the saved set; put the imported one in after it.
          jsonArea.value = JSON.stringify(imported, null, 2);
          setState('unsaved', t('editor.unsaved'));
          toast(t('editor.imported'));
        })
        .catch((err: unknown) => {
          toastError(err, t('editor.badJson'));
        });
    });

    qs(main, '[data-export]').addEventListener('click', () => {
      const blob = new Blob(
        [JSON.stringify({ gameId, title: titleInput.value.trim(), set: currentSet }, null, 2)],
        { type: 'application/json' },
      );
      downloadBlob(
        blob,
        `${(titleInput.value.trim() || game.name).replace(/[^\p{L}\p{N}_-]+/gu, '_')}.json`,
      );
    });
  })();

  /**
   * Leaving the page (tab closed, reload) inside a save delay would lose
   * the last change. On `pagehide` the editors flush their pending save
   * (EditorContext.save), a pending title save goes out too, and both use
   * keepalive requests, which the browser finishes after the page is gone.
   * Keepalive bodies are capped at 64 KB; a failure there is silent and the
   * debounce has usually saved long before.
   */
  const onPageHide = (): void => {
    leaving = true;
    if (titleTimer !== 0) {
      window.clearTimeout(titleTimer);
      titleTimer = 0;
      titleSave?.();
    }
  };
  const onPageShow = (): void => {
    leaving = false;
  };
  window.addEventListener('pagehide', onPageHide);
  window.addEventListener('pageshow', onPageShow);

  return () => {
    window.removeEventListener('pagehide', onPageHide);
    window.removeEventListener('pageshow', onPageShow);
    life.dispose();
    window.clearTimeout(titleTimer);
    editor?.destroy();
  };
}
